/**
 * Department structure (D2, workspace docs/guardian/03 §3.2, 2026-10-22):
 * stations, apparatus, unit call signs, mutual-aid partners, dispatch centers
 * and channel names Guardian should recognise (and bias voice toward). Admins
 * add items and approve/reject/retire each; members see the approved set
 * read-only. All text is rendered as text only. A 503 `terms_unavailable`
 * hides the section.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  addDepartmentStructure,
  approveDepartmentStructure,
  fetchDepartmentStructure,
  rejectDepartmentStructure,
  retireDepartmentStructure,
} from '../api/guardianApi';
import { departmentErrorMessage } from '../utils/departmentErrors';

export const KINDS = [
  ['station', 'Stations'],
  ['apparatus', 'Apparatus'],
  ['unit_callsign', 'Unit call signs'],
  ['mutual_aid_partner', 'Mutual-aid partners'],
  ['dispatch_center', 'Dispatch centers'],
  ['channel_name', 'Channel names'],
];

export function kindLabel(kind) {
  return (KINDS.find(([v]) => v === kind) || [null, kind])[1];
}

function AddStructure({ onAdd }) {
  const [kind, setKind] = useState('station');
  const [localName, setLocalName] = useState('');
  const [canonicalType, setCanonicalType] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const save = async () => {
    setBusy(true);
    setError(null);
    const body = { kind, localName: localName.trim() };
    if (canonicalType.trim()) body.canonicalType = canonicalType.trim();
    const err = await onAdd(body);
    setBusy(false);
    if (err) {
      setError(err);
      return;
    }
    setLocalName('');
    setCanonicalType('');
  };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <Select size="small" value={kind} onChange={(e) => setKind(e.target.value)} inputProps={{ 'aria-label': 'Kind' }}>
          {KINDS.map(([v, label]) => <MenuItem key={v} value={v}>{label}</MenuItem>)}
        </Select>
        <TextField size="small" label="Name" value={localName} onChange={(e) => setLocalName(e.target.value)} inputProps={{ maxLength: 120 }} sx={{ flexGrow: 1, minWidth: 180 }} />
        <TextField size="small" label="Standard type (optional)" value={canonicalType} onChange={(e) => setCanonicalType(e.target.value)} inputProps={{ maxLength: 120 }} />
        <Button variant="contained" onClick={save} disabled={busy || !localName.trim()}>Add</Button>
      </Box>
    </Box>
  );
}

/**
 * @param {object} props
 * @param {string|null} props.authToken
 * @param {boolean} [props.isAdmin]
 */
export default function DepartmentStructure({ authToken, isAdmin = false }) {
  const [tab, setTab] = useState(isAdmin ? 'draft' : 'approved');
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [off, setOff] = useState(false);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const load = useCallback(async () => {
    const res = await fetchDepartmentStructure(authToken, isAdmin ? tab : 'approved');
    if (!alive.current) return;
    if (res?.success) setItems(Array.isArray(res.data?.structure) ? res.data.structure : []);
    else if (res?.data?.code === 'terms_unavailable') setOff(true);
    else setError(departmentErrorMessage(res, 'Could not load your department structure.'));
  }, [authToken, isAdmin, tab]);
  useEffect(() => { load(); }, [load]);

  const act = async (call, fallback) => {
    setError(null);
    const res = await call();
    if (!res?.success) {
      setError(departmentErrorMessage(res, fallback));
    }
    load();
  };

  const onAdd = async (body) => {
    setError(null);
    const res = await addDepartmentStructure(authToken, body);
    if (!res?.success) return departmentErrorMessage(res, 'Could not add that item.');
    load();
    return null;
  };
  const onApprove = (it) => act(() => approveDepartmentStructure(authToken, it.id, it.rev), 'Could not approve the item.');
  const onReject = (it) => act(() => rejectDepartmentStructure(authToken, it.id, it.rev), 'Could not reject the item.');
  const onRetire = (it) => act(() => retireDepartmentStructure(authToken, it.id, it.rev), 'Could not retire the item.');

  if (off) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="h6" component="h2">Stations and apparatus</Typography>
      <Typography variant="body2" color="text.secondary">
        {isAdmin
          ? 'Tell Guardian your stations, apparatus, call signs, mutual-aid partners, dispatch centers and radio channels so it understands and hears them correctly.'
          : 'Your department’s stations, apparatus, call signs and partners.'}
      </Typography>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {isAdmin && (
        <>
          <AddStructure onAdd={onAdd} />
          <Tabs value={tab} onChange={(_, v) => setTab(v)} aria-label="Structure views" variant="scrollable">
            <Tab value="draft" label="To review" />
            <Tab value="approved" label="Approved" />
            <Tab value="rejected" label="Rejected" />
            <Tab value="retired" label="Retired" />
          </Tabs>
        </>
      )}

      {KINDS.map(([kind, label]) => {
        const group = items.filter((it) => it.kind === kind);
        if (group.length === 0) return null;
        return (
          <Box key={kind}>
            <Typography variant="subtitle2" sx={{ mt: 0.5 }}>{label}</Typography>
            <Box component="ul" sx={{ m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
              {group.map((it) => (
                <Paper key={`${it.id}-${it.rev}`} variant="outlined" component="li" sx={{ p: 1.25, listStyle: 'none', display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{it.localName}</Typography>
                    {it.canonicalType && <Typography variant="caption" color="text.secondary">{it.canonicalType}</Typography>}
                  </Box>
                  {isAdmin && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {it.status === 'draft' && (
                        <>
                          <Button size="small" variant="contained" onClick={() => onApprove(it)}>Approve</Button>
                          <Button size="small" color="error" onClick={() => onReject(it)}>Reject</Button>
                        </>
                      )}
                      {it.status === 'approved' && (
                        <Button size="small" color="error" onClick={() => onRetire(it)}>Retire</Button>
                      )}
                    </Box>
                  )}
                  {!isAdmin && it.status && it.status !== 'approved' && (
                    <Chip size="small" variant="outlined" label={it.status} />
                  )}
                </Paper>
              ))}
            </Box>
          </Box>
        );
      })}
      {items.length === 0 && <Typography variant="body2" color="text.secondary">Nothing here yet.</Typography>}
    </Paper>
  );
}
